﻿<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import { get, set, AccountSettingDefinition } from './AccountSettings.access';
    import Loading from '@shared/components/Loading.vue';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@cwd/shared/components/Form/Form';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const state = reactive<{
        isLoading: boolean;
        settings: AccountSettingDefinition[] | null;
        form: FormProps | null;
    }>({
        isLoading: false,
        settings: null,
        form: null,
    });

    onMounted(async () => {
        await getSettings();
    });

    async function getSettings(): Promise<void> {
        state.isLoading = true;

        await get()
            .then((settings) => {
                state.settings = settings;

                initForm();
            })
            .finally(() => (state.isLoading = false));
    }

    async function setSettings(): Promise<void> {
        !state.form?.disableSubmit && (state.isLoading = true);

        const accountSettings = state.settings!.map((s) => s.AccountSetting);

        await set(accountSettings)
            .then((settings) => {
                // this breaks second save
                // state.settings = settings
                // initForm()

                state.form!.alert = { title: $Resources.AlertSuccessHeading, content: $Resources.AlertSuccessBody };
            })
            .catch((error) => {
                state.form!.alert = { type: 'danger', content: $Resources.ErrorLabel };
            })
            .finally(() => {
                state.isLoading = false;
                state.form!.disableSubmit = false;
            });
    }

    function initForm(): void {
        state.settings = state.settings!.filter((setting) => setting.Feature == null || featureAccess.hasFeature(setting.Feature));

        state.form = {
            disableSubmit: false,
            alert: null,
            data: {},
        };

        state.settings!.forEach((setting) => {
            const key = setting.Key.replace('.', '');

            switch (setting.Type) {
                case 'Bool':
                    state.form!.data[key] = {
                        modelValue: setting.AccountSetting?.Value?.toLowerCase() == 'true',
                        required: false,
                        group: setting.Group != null ? $Resources['AccountSettingsGroupLabel' + setting.Group] : null,
                        checkbox: {
                            label: $Resources['AccountSettingsLabel' + key],
                            switchStyle: 'standard',
                        },
                    };
                    break;

                default:
                    break;
            }
        });
    }

    const onSubmit = (event: Event, formData: any) => {
        state.form!.disableSubmit = true;

        state.settings!.forEach((setting) => {
            const key = setting.Key.replace('.', '');
            const newValue = formData[key];

            if (newValue != null) {
                if (setting.AccountSetting != null) {
                    setting.AccountSetting.Value = newValue;
                } else {
                    setting.AccountSetting = {
                        Key: setting.Key,
                        Value: newValue,
                    };
                }
            }
        });

        setSettings();
    };
</script>

<template>
    <div class="c-progress-bar-top-of p-sm-t">
        <Loading :show="state.isLoading" loading-style="progress-bar"></Loading>

        <div v-if="state.settings?.length == 0">
            <div class="alert alert-info alert-fixed-i m-sm-b">
                <p>
                    <i class="fa fa-info-circle" aria-hidden="true"></i>
                    <span>{{ $Resources.NoSettingsAvailable }}</span>
                </p>
            </div>
        </div>

        <div v-if="state.form != null && state.settings && state.settings.length > 0">
            <Form
                :data="state.form.data"
                :alert="state.form.alert"
                :submitting="state.form.disableSubmit"
                @update:modelValue="(key, newVal) => (state.form!.data[key].modelValue = newVal)"
                @onSubmit="onSubmit"></Form>
        </div>
    </div>
</template>

<style></style>
